/**
 * Grupos de interés (`/api/grupos-interes`).
 *
 * La población encuestada: estudiantes, docentes, egresados, empleadores. Cada
 * grupo asociado a un instrumento genera un formulario de Google independiente.
 * Los instrumentos documental y cuantitativa NO los admiten (el backend responde
 * 422).
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type GrupoInteres from '@/interfaces/GrupoInteres'
import { http } from '@/services/http'

/**
 * Consulta grupo interes · petición `GET`.
 *
 * @returns GrupoInteres[]
 */
export async function getGruposInteresService(url: string): Promise<GrupoInteres[]> {
  const result = await http.get<GrupoInteres[]>(url)
  if (!result.ok) throw new Error('Error al listar grupos de interés')
  return result.data ?? []
}

/**
 * Consulta grupo interes · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns GrupoInteres | null
 */
export async function getGrupoInteresByIdService(url: string, id: number): Promise<GrupoInteres | null> {
  const result = await http.get<GrupoInteres>(`${url}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Crea grupo interes · petición `POST`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns GrupoInteres | null
 */
export async function crearGrupoInteresService(
  url: string,
  payload: Omit<GrupoInteres, 'id_grupo_interes'>
): Promise<GrupoInteres | null> {
  const result = await http.post<GrupoInteres>(url, payload)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Actualiza grupo interes · petición `PUT`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns GrupoInteres | null
 */
export async function actualizarGrupoInteresService(
  url: string,
  id: number,
  payload: Partial<GrupoInteres>
): Promise<GrupoInteres | null> {
  const result = await http.put<GrupoInteres>(`${url}/${id}`, payload)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Elimina grupo interes · petición `DELETE`.
 *
 * @returns boolean
 */
export async function eliminarGrupoInteresService(url: string, id: number): Promise<boolean> {
  const result = await http.delete(`${url}/${id}`)
  if (result.status === 422) {
    const msg = Object.values(result.errors ?? {}).flat()[0]
    if (msg) console.warn(msg)
    return false
  }
  return result.ok
}

/**
 * Asigna grupo interes · `POST /api/instrumentos`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns boolean
 */
export async function asignarGruposAInstrumentoService(idInstrumento: number, grupos: number[]): Promise<boolean> {
  const result = await http.post(`/api/instrumentos/${idInstrumento}/grupos`, { grupos })
  return result.ok
}

/**
 * Elimina grupo interes · `DELETE /api/instrumentos`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns boolean
 */
export async function desvincularGrupoDeInstrumentoService(idInstrumento: number, idGrupo: number): Promise<boolean> {
  const result = await http.delete(`/api/instrumentos/${idInstrumento}/grupos/${idGrupo}`)
  return result.ok
}
